import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import { TurnOrder } from 'boardgame.io/core';
import type { AoSGameState } from '../../types';
import { issueSharesMove } from '../moves/issueShares';
import { undoToTurnStart } from '../moves/undoToTurnStart';
import { createTurnStartSnapshot } from '../utils/turnStartSnapshot';

/**
 * Phase 1: 株式発行フェーズ（全マップ共通）
 *
 * config で制御:
 * - maxSharesLimit: 総発行上限（デフォルト: 15、Detroit: 25）
 * - maxSharesPerTurn: 1ターン発行上限（Barbados: 1、他: undefined = 無制限）
 *
 * ソロプレイのため、1回の操作で即座にPhase終了。
 *
 * @see docs/specifications/logic/common/phase_01_issue_shares.md
 */
export const issueSharesPhase: PhaseConfig<AoSGameState> = {
  start: true, // ゲーム開始時のフェーズ

  moves: {
    issueShares: issueSharesMove,
    undoToTurnStart,
  },

  turn: {
    // ソロプレイのため、各プレイヤーは1回だけ株式発行可能
    moveLimit: 1,

    // ターン順序: ソロプレイなのでDEFAULT
    order: TurnOrder.DEFAULT
  },

  onBegin: ({ G }: { G: AoSGameState }) => {
    // Undo からの復帰の場合はターン番号をインクリメントしない
    // （スナップショットから復元済みのため）
    if (G.forcePhaseToIssueShares) {
      debug.log(`[issueSharesPhase] Phase 1: Restored from Undo (Turn ${(G as any).actualTurn})`);
      G.forcePhaseToIssueShares = false;
    } else {
      // 通常のターン進行: ターン番号をインクリメント（初期値0からスタート）
      const previousTurn = (G as any).actualTurn ?? 0;
      (G as any).actualTurn = previousTurn + 1;
      debug.log(`[issueSharesPhase] Phase 1: Issue Shares started (Turn ${(G as any).actualTurn})`);
    }

    // ターン開始時のスナップショットを保存（Undo 用）
    G.turnStartSnapshot = createTurnStartSnapshot(G);
    debug.log(`[issueSharesPhase] Turn start snapshot saved`);

    // 自動フェーズ履歴をクリア（新ターン開始）
    G.completedAutoPhases = [];

    // 自動フェーズ結果をクリア
    G.autoPhaseResult = null;

    // フラグをリセット
    Object.values(G.players).forEach(player => {
      player.hasExecutedIssueSharesMove = false;
      // ターン発行数もリセット（maxSharesPerTurn 対応）
      if (player.currentTurnSharesIssued !== undefined) {
        player.currentTurnSharesIssued = 0;
      }
    });
  },

  // Phase終了条件: issueSharesMove実行済みなら終了
  endIf: ({ G }: { G: AoSGameState }) => {
    const player = G.players?.['0'];
    if (!player) {
      return false;
    }

    return player.hasExecutedIssueSharesMove === true;
  },

  // Phase 2（プレイ順決定）をスキップするため、Phase 3へ遷移
  next: 'selectActions'
};
